import { GoogleLogin } from '@react-oauth/google'
import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import googleLoginButtonStyles from './googleLoginButton.styles'
import { googleLogin } from '../../../../../Apis/googleLogin/googleLogin'
import completeSsoSignup from '../../../../../Apis/auth/completeSsoSignup'
import { useMutation } from 'react-query'
import { useHistory, useLocation } from 'react-router-dom'
import { useNotify } from 'react-admin'
import SsoConsentDialog from '../../../../SsoConsentDialog/SsoConsentDialog'
import startSession from '../../../../../utils/helpers/startSession'
import afterLoginPath from '../../../../../utils/helpers/afterLoginPath'

// SSO bypasses authProvider.login, so it must establish the session the same
// way (including dropping the previous user's query cache).
const persistTokensAndRedirect = async (data, history, location) => {
  await startSession(data)
  history.push(afterLoginPath(location))
}

const GoogleLoginButton = () => {
  const history = useHistory()
  const location = useLocation()
  const notify = useNotify()
  const useStyles = makeStyles(googleLoginButtonStyles)
  const classes = useStyles()

  const [pendingConsent, setPendingConsent] = useState(null)

  const googleLoginRequest = useMutation((data) => googleLogin(data), {
    onSuccess: (data) => {
      // New SSO user: hold the pending-signup token in state and pop the consent
      // dialog. Backend has not created a User row at this point.
      if (data?.status === 'consent_required') {
        setPendingConsent({
          pendingSignupToken: data.pendingSignupToken,
          profile: data.profile,
          tos: data.tos,
        })
        return
      }
      persistTokensAndRedirect(data, history, location)
    },
  })

  const completeSsoSignupRequest = useMutation(
    (payload) => completeSsoSignup(payload),
    {
      onSuccess: (data) => {
        setPendingConsent(null)
        persistTokensAndRedirect(data, history, location)
      },
      onError: (error) => {
        // Auto-recovery: backend told us the pending-signup token expired (or
        // was otherwise invalid). Close the dialog and route the user back to
        // re-do Google login, with a clearer message than the generic error.
        const code = error?.response?.data?.code
        const message = error?.response?.data?.message
        if (code === 'pending_signup_expired' || code === 'pending_signup_invalid') {
          setPendingConsent(null)
          notify(message, 'warning', undefined, false, 6000)
          return
        }
        notify('Could not complete signup. Please try again.', 'error', undefined, false, 5000)
      },
    },
  )

  const successResponse = async (response) => {
    googleLoginRequest.mutate(response)
  }

  const errorMessage = () => {
    notify(
      'Something went wrong connecting with your google account!',
      'error',
      undefined,
      false,
      5000,
    )
  }

  return (
    <div className={classes.buttonContainer}>
      <GoogleLogin
        onSuccess={successResponse}
        onError={errorMessage}
        theme='filled_blue'
        text='signin_with'
        // Google draws the button at a fixed pixel width; match the form, minus its gutter on a phone.
        width={String(Math.min(350, window.innerWidth - 32))}
      />
      <SsoConsentDialog
        open={!!pendingConsent}
        profile={pendingConsent?.profile}
        tos={pendingConsent?.tos}
        submitting={completeSsoSignupRequest.isLoading}
        onAccept={({ marketingConsent, dataSharingConsent }) =>
          completeSsoSignupRequest.mutate({
            pendingSignupToken: pendingConsent.pendingSignupToken,
            marketingConsent,
            dataSharingConsent,
          })
        }
        onCancel={() => setPendingConsent(null)}
      />
    </div>
  )
}

export default GoogleLoginButton
